import React, { useEffect } from "react";

import classes from "./AcademicYearFilter.scss";

import _ from "lodash";

import { connect } from "react-redux";

import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  updateAcademicYearFilter,
  getCurrentProjectGroupDetails,
} from "Projects/modules/ProjectModules";
import { getYearDurationFormat } from "Utils";
import {
  getOrganizationAcademicYearIds,
  getCurrentProjectGroupAcademicYearDetails,
} from "Projects/modules/Utils";
import { getSelectedAcademicYear } from "modules/CommonSelectors";
import { getProjectGroupAcademicYearsQuery } from "Projects/modules/ProjectQueries";
import { compose, graphql } from "lib/apolloClient";
import SkeletonLoader from "UIComponents/SkeletonLoader";

import { useI18n } from "Hooks";

import {
  Dropdown,
  DropdownMenu,
  ButtonDisclosure,
  DSProvider,
} from "@toddle-edu/ds-web";

import { CalendarScheduleOutlined } from "@toddle-edu/ds-icons";

const AcademicYearFilter = props => {
  const {
    currentAndPreviousAYs,
    currentAndPreviousAYIds,
    updateAcademicYearFilter,
    academicYearFilterIds,
    currentAcademicYearId,
    projectGroupId,
    academicYearId,
    isAcademicYearsLoaded,
  } = props;

  const { t } = useI18n();

  const defaultSelectedFilter = _.find(currentAndPreviousAYs, {
    isCurrentAcademicYear: true,
  })?.id;

  useEffect(() => {
    if (
      _.isEmpty(academicYearFilterIds) &&
      !_.isEmpty(currentAndPreviousAYIds)
    ) {
      updateAcademicYearFilter({
        academicYearIds: !currentAcademicYearId
          ? [academicYearId]
          : defaultSelectedFilter
          ? [defaultSelectedFilter]
          : [],
        isCurrentAcademicYearSelected: !!currentAcademicYearId,
      });
    }
  }, [
    projectGroupId,
    currentAcademicYearId,
    currentAndPreviousAYIds,
    academicYearFilterIds,
  ]);

  const academicYearOptions = _.map(
    currentAndPreviousAYs,
    currentAndPreviousAY => {
      const { startDate, endDate, id, isCurrentAcademicYear = false } =
        currentAndPreviousAY || {};

      const yearDuration = getYearDurationFormat({ startDate, endDate });

      const label = t("project:ay_with_label", { label: yearDuration });

      return {
        key: id,
        label,
        subText: isCurrentAcademicYear ? t("common:current") : null,
      };
    }
  );

  const isSingleAcademicYear = _.size(currentAndPreviousAYIds) === 1;

  const updatedOptions = [
    ...academicYearOptions,
    ...(!isSingleAcademicYear
      ? [
          { isDivider: true },
          {
            key: "all",
            label: t("project:all_academic_years"),
          },
        ]
      : []),
  ];

  const selectedFilterKey = _.isEmpty(academicYearFilterIds)
    ? defaultSelectedFilter
    : _.size(academicYearFilterIds) === 1
    ? _.first(academicYearFilterIds)
    : "all";

  const selectedFilterLabel = _.find(updatedOptions, { key: selectedFilterKey })
    ?.label;

  const handleFilterClick = ({ key }) => {
    const selectedAcademicYearIds =
      key === "all" ? currentAndPreviousAYIds : [key];

    const selectedFilterLabel = _.find(updatedOptions, { key })?.label;

    updateAcademicYearFilter({
      academicYearIds: selectedAcademicYearIds,
      isCurrentAcademicYearSelected:
        _.size(selectedAcademicYearIds) === 1 &&
        _.first(selectedAcademicYearIds) === currentAcademicYearId,
      academicYearName: selectedFilterLabel,
    });
  };

  if (_.isEmpty(currentAndPreviousAYIds) && isAcademicYearsLoaded) {
    return null;
  }

  if (_.isEmpty(currentAndPreviousAYIds)) {
    return (
      <div className={classes.skeletonLoader}>
        <SkeletonLoader height="36px" width="160px" />
      </div>
    );
  }

  return (
    <DSProvider dsVersion="2.0">
      <Dropdown
        showSelection={true}
        overlay={
          <DropdownMenu
            options={updatedOptions}
            showSelection
            onClick={handleFilterClick}
            value={selectedFilterKey}
          />
        }
      >
        <ButtonDisclosure
          variant="neutral"
          type="outlined"
          icon={<CalendarScheduleOutlined />}
          testId="project-academic-year-filter"
        >
          {selectedFilterLabel}
        </ButtonDisclosure>
      </Dropdown>
    </DSProvider>
  );
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  const { org_id: organizationId } = userInfo;

  const organizationAcademicYearIds = getOrganizationAcademicYearIds(
    organizationId
  );

  const academicYearFilterIds = _.get(
    state,
    "project.academicYearFilterDetails.academicYearIds",
    []
  );

  const { id: projectGroupId } = getCurrentProjectGroupDetails({
    state,
  });

  const {
    currentAndPreviousAYs,
    currentAndPreviousAYIds,
    currentAcademicYearId,
    isAcademicYearsLoaded,
  } = getCurrentProjectGroupAcademicYearDetails({
    organizationId,
    projectGroupId,
  });

  const academicYearSelected = getSelectedAcademicYear(state);

  const { id: academicYearId } = academicYearSelected;

  return {
    currentAndPreviousAYs,
    currentAndPreviousAYIds,
    academicYearFilterIds,
    currentAcademicYearId,
    projectGroupId,
    organizationAcademicYearIds,
    academicYearId,
    isAcademicYearsLoaded,
  };
};

const mapActionCreators = {
  updateAcademicYearFilter,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getProjectGroupAcademicYearsQuery, {
    name: "getProjectGroupAcademicYears",
    options: ({ projectGroupId, organizationAcademicYearIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: projectGroupId,
        filters: {
          academicYearIds: organizationAcademicYearIds,
        },
      },
    }),
  })
)(AcademicYearFilter);
